class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector(
      '[data-predictive-search]'
    );
    this.allPredictiveSearchInstances =
      document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (
      !this.getQuery().length ||
      this.querySelector('[aria-selected="true"] a')
    )
      event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector(
      '[data-predictive-search-search-for-text]'
    );
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, 'g')).length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(
      this.querySelectorAll('li, button.predictive-search__item')
    ).filter((element) => element.offsetParent !== null);
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex =
        selectedElementIndex === allVisibleElements.length - 1
          ? 0
          : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex =
        selectedElementIndex === 0
          ? allVisibleElements.length - 1
          : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector(
      '[aria-selected="true"] a, button[aria-selected="true"]'
    );

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(' ', '-').toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    fetch(
      `${routes.predictive_search_url}?q=${encodeURIComponent(
        searchTerm
      )}&section_id=predictive-search`,
      {
        signal: this.abortController.signal,
      }
    )
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML;
        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach(
          (predictiveSearchInstance) => {
            predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
          }
        );
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error?.code === 20) {
          // Code 20 means the call was aborted
          return;
        }
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement =
      this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText =
      this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    this.predictiveSearchLinks =
      this.predictiveSearchResults.querySelectorAll('a, button');
    this.predictiveSearchLinks.forEach((link) => {
      link.setAttribute('tabindex', '-1');
    });

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(
      this.querySelector('[data-predictive-search-live-region-count-value]')
        .textContent
    );
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight -
      document.querySelector('.section-header')?.getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight =
      this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);

class AlgoliaSearch extends HTMLElement {
  constructor() {
    super();

    setTimeout(() => {
      if(window.algoliaShopify && window.algoliaShopify.config) { 
        this.init();
      }
    }, 10);    
  }

  init() {
    // Algolia search client (vendored v5 lite: assets/algoliasearch-lite.umd.js)
    const lite = window['algoliasearch/lite'];
    if (!lite || typeof lite.liteClient !== 'function') {
      console.warn('[Algolia] client unavailable');
      return;
    }

    this.searchClient = lite.liteClient(
      algoliaShopify.config.app_id,
      algoliaShopify.config.search_api_key,
      {
        baseHeaders: {
          'X-Algolia-UserToken': getAlgoliaUserToken(),
        },
      }
    );

    this.productIndexName = `${algoliaShopify.config.index_prefix}products${
      window.theme.locale == 'fr_CA' ? '_canada_fr' : ''
    }`;
    this.collectionIndexName = `${algoliaShopify.config.index_prefix}collections`;

    this.query = '';

    // DOM elements
    this.searchContainer = this.querySelector('.predictive-search');
    this.collectionsContainer = this.querySelector(
      '#PredictiveSearchCollections'
    );
    this.productsContainer = this.querySelector('#PredictiveSearchProducts');
    this.searchInput = this.querySelector('input[type="search"]');
    this.statusElement = this.querySelector('.predictive-search-status');

    // Events
    this.searchInput.addEventListener('input', this.onSearchInput.bind(this));
    this.searchInput.addEventListener('focus', this.onSearchFocus.bind(this));
    this.searchInput.addEventListener('keydown', this.onKeydown.bind(this));
    this.searchInput.addEventListener('keyup', this.onKeyup.bind(this));

    this.addEventListener('focusout', this.onFocusOut.bind(this));
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  onKeyup(event) {
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex =
        selectedElementIndex === allVisibleElements.length - 1
          ? 0
          : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex =
        selectedElementIndex === 0
          ? allVisibleElements.length - 1
          : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector(
      '[aria-selected="true"] a, button[aria-selected="true"]'
    );

    if (selectedOption) selectedOption.click();
  }

  onSearchInput(event) {
    this.query = event.target.value;

    if (window.theme.dynamic_yield_enabled && DynamicYield) {
      DynamicYield.keywordSearch(this.query);
    }

    this.search();
  }

  onSearchFocus(event) {
    this.search();
  }

  onFocusOut(event) {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) {
        this.close();
      }
    });
  }

  search() {
    const context = [];

    if (this.query.length === 0) {
      context.push('empty_search');
    }

    // Products and collections in one request: results[0] = products, results[1] = collections
    this.searchClient
      .search({
        requests: [
          {
            indexName: this.productIndexName,
            query: this.query,
            hitsPerPage: 5,
            attributesToRetrieve: ['*'],
            ruleContexts: context,
            clickAnalytics: true,
            userToken: getAlgoliaUserToken(),
          },
          {
            indexName: this.collectionIndexName,
            query: this.query,
            hitsPerPage: 5,
            attributesToRetrieve: ['*'],
            ruleContexts: context,
            userToken: getAlgoliaUserToken(),
          },
        ],
      })
      .then(({ results }) => {
        const response = results[0];
        const hits = response.hits;
        this.productsContainer.innerHTML = '';

        hits.forEach((hit, index) => {
          const li = document.createElement('li');
          li.classList.add('grid__item');

          if(index >= 4) {
            li.classList.add('hidden--mobile');
          }

          const card = document.createElement('div');
          card.classList.add('card');

          const a = document.createElement('a');
          a.classList.add('body');
          a.classList.add('link');
          a.href = `${window.theme.locale == 'fr_CA' ? '/fr' : ''}/products/${
            hit.handle
          }`;
          a.addEventListener('click', (event) => {
            if (aa) {
              aa('clickedObjectIDsAfterSearch', {
                userToken: getAlgoliaUserToken(),
                eventName: 'Autocomplete: Product Clicked',
                index: `${algoliaShopify.config.index_prefix}products`,
                queryID: response.queryID,
                objectIDs: [hit.objectID],
                positions: [hit.position],
              });

              addToAlgoliaStorage(hit.id, response.queryID, `${algoliaShopify.config.index_prefix}products`);
            }
          });

          const imageWrapper = document.createElement('div');
          imageWrapper.classList.add('card__image-wrapper');

          const imageContainer = document.createElement('div');
          imageContainer.classList.add('card__image-container');

          const media = document.createElement('div');
          media.classList.add('media', `media--${window.cards.aspect_ratio}`);

          const image = document.createElement('img');
          image.classList.add('motion-reduce');
          image.loading = 'lazy';
          image.width = 240;
          image.height = 240;
          image.src = hit.image + '&width=240';
          image.alt = hit.title;

          const a2 = document.createElement('a');
          a2.classList.add('body', 'link', 'link--naked');
          a2.href = `${window.theme.locale == 'fr_CA' ? '/fr' : ''}/products/${
            hit.handle
          }`;
          a2.addEventListener('click', (event) => {
            if (aa) {
              aa('clickedObjectIDsAfterSearch', {
                userToken: getAlgoliaUserToken(),
                eventName: 'Autocomplete: Product Clicked',
                index: `${algoliaShopify.config.index_prefix}products`,
                queryID: response.queryID,
                objectIDs: [hit.objectID],
                positions: [hit.position],
              });

              addToAlgoliaStorage(hit.id, response.queryID, `${algoliaShopify.config.index_prefix}products`);
            }
          });

          const details = document.createElement('div');
          details.classList.add('card__details');

          const vendor = document.createElement('div');
          vendor.classList.add('card__vendor', 'body');
          vendor.innerHTML = `${hit.vendor}`;
          details.appendChild(vendor);

          const title = document.createElement('div');
          title.classList.add('card__title');
          const span = document.createElement('a');
          span.href = `${window.theme.locale == 'fr_CA' ? '/fr' : ''}/products/${
            hit.handle
          }`;
          span.classList.add('card__title-title');
          span.innerHTML = hit.title;

          title.appendChild(span);
          details.appendChild(title);

          const priceContainer = document.createElement('div');
          priceContainer.classList.add('card__price');

          const price = document.createElement('div');
          price.classList.add('price');
          if (hit.compare_at_price > hit.price) {
            price.classList.add('price--on-sale');
          }

          price.innerHTML = `
            <div class="price__sale">
              <span class="visually-hidden visually-hidden--inline">Sale Price</span>
              <span class="price-item">
                $${hit.price} ${Shopify.currency.active}
              </span>
            </div>            

            ${
              hit.compare_at_price > hit.price
                ? `
              <div class="price__regular body body--small gray">
                <span class="visually-hidden visually-hidden--inline">Regular Price</span>
                <s class="price-item">
                  $${hit.compare_at_price} ${Shopify.currency.active}
                </s>
              </div>
            `
                : ''
            }
          `;

          priceContainer.appendChild(price);
          details.appendChild(priceContainer);

          media.appendChild(image);
          imageContainer.appendChild(media);
          imageWrapper.appendChild(imageContainer);    

          a.appendChild(imageWrapper);
          a2.appendChild(details);

          card.appendChild(a);
          card.appendChild(a2);

          li.appendChild(card);

          this.productsContainer.appendChild(li);
        });

        this.collectionsContainer.innerHTML = '';

        results[1].hits.forEach((hit) => {
          const li = document.createElement('li');
          li.classList.add('predictive-search__item');

          const a = document.createElement('a');
          a.classList.add('body', 'link', 'link--unstyled');
          a.href = `${window.theme.locale == 'fr_CA' ? '/fr' : ''}/collections/${
            hit.handle
          }`;
          a.textContent = hit.title;

          li.appendChild(a);

          this.collectionsContainer.appendChild(li);
        });
      })
      .catch((error) => {
        console.warn('[Algolia] predictive search failed', error);
      });

    this.open();
  }

  open() {
    this.setAttribute('open', true);
    this.searchInput.setAttribute('aria-expanded', true);
  }

  close() {
    this.removeAttribute('open');
    this.searchInput.setAttribute('aria-expanded', false);
  }
}

customElements.define('algolia-search', AlgoliaSearch);
